import Dropdown from 'app/components/Dropdown';
import FilterModel from 'app/components/FilterModel';
import NewInput from 'app/components/NewInput';
import PageLoader from 'app/components/PageLoader';
import NumberInput from 'app/components/UI/NumberInput';
import Button from 'app/components/button';
import PaginatedTable from 'app/components/table/PaginationTable';
import { voucherTableHeader } from 'app/constants/table-header-constants';
import { useMemo } from 'react';
import { Modal } from 'react-bootstrap';
import { RxCross2 } from 'react-icons/rx';
import { useSearchParams } from 'react-router-dom';
import Packages from './Packages';
import useVoucherManagement from './useVoucherManagement';

const GAMIFICATION_FILTER_OPTIONS = [
  { label: 'Regular & Game Vouchers', value: 'all' },
  { label: 'Regular Vouchers', value: false },
  { label: 'Game Vouchers', value: true },
  { label: 'Bundle Vouchers', value: 'package' },
];

const VouchersPane = () => {
  const {
    isdeleteFiltering,
    pageLoading,
    dataPerPage,
    totalNumber,
    pageCount,
    navigate,
    sectionObj,
    filteredData,
    fetchVoucherData,
    handleComplimentaryVoucherSubmit,
    values,
    setChange,
    resetValues,
    showModal,
    setShowModal,
    error,
    setError,
    onHideModal,
    selectedVoucher,
    filterValues,
    setFilterValues,
    appliedFilters,
    isFilterOpen,
    setIsFilterOpen,
    isFiltering,
    handleFilterSubmit,
    handleFilterCancel,
    resetFilter,
    passwordmodelOpen,
    setPasswordmodelOpen,
    handlemodelvalues,
    passwordVal,
    setPasswordVal,
    handlePasswordModel,
    handleComplimentaryAction,
    handleNumber
  } = useVoucherManagement();



  const changemm = () => setPasswordmodelOpen(false);

  const modalContent = useMemo(
    () => (
      <Modal
        size='sm'
        show={passwordmodelOpen}
        onHide={changemm}
        aria-labelledby='delete-popup'
        centered
      >
        <div className='cursor-pointer'>
          <span className='close-icon' onClick={changemm}>
            <RxCross2 />
          </span>
        </div>
        <Modal.Body className='text-center d-flex justify-content-around mt-5'>
          <div className='modal-image-form'>
            <NewInput
              label='Password*'
              type='text'
              placeholder='Enter Password'
              value={passwordVal}
              onChange={(e) => setPasswordVal(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && passwordVal) handlemodelvalues();
              }}
            />
            <Button
              className='mt1 button ms-2'
              onClick={handlemodelvalues}
              variant='Submit'
              name='Submit'
              isDisabled={!passwordVal}
            />
          </div>
        </Modal.Body>
      </Modal>
    ),
    [passwordmodelOpen, handlemodelvalues, passwordVal]
  );

  // The Package column is blank for every voucher that is not part of a
  // package, which is almost all of them, so it only earns its width when the
  // admin has actually filtered to "Package Vouchers". Memoised because
  // PaginationTable re-fetches in a loop if the columns array changes identity
  // on every render.
  const voucherColumns = useMemo(
    () =>
      appliedFilters?.isGamificationVoucher === 'package'
        ? voucherTableHeader
        : voucherTableHeader.filter((col) => col.accessor !== 'packageName'),
    [appliedFilters?.isGamificationVoucher]
  );

  return (
    <>
      <div className='cms-content__container'>
        <PaginatedTable
          sectionObj={sectionObj}
          columns={voucherColumns}
          data={filteredData}
          fulldata={filteredData}
          dataPerPage={dataPerPage}
          totalNumber={totalNumber}
          pageCount={pageCount}
          handleDelete={(row) => handlePasswordModel(row, 'delete')}
          handleEdit={(row) => handlePasswordModel(row, 'edit')}
          pageLoading={pageLoading}
          navigate={navigate}
          fetchData={fetchVoucherData}
          isdeleteFiltering={isdeleteFiltering}
          isFiltering={isFiltering}
          values={appliedFilters}
          handleComplimentaryAction={handleComplimentaryAction}
          // Presentation only, never authorisation: the server refuses edit,
          // delete and complimentary for a package member voucher.
          isRowActionsHidden={(row) => row?.packageId != null}
        />

        {isFilterOpen && (
          <FilterModel
            isFilterOpen={isFilterOpen}
            setIsFilterOpen={setIsFilterOpen}
            onSubmit={handleFilterSubmit}
            onCancel={handleFilterCancel}
            resetfilter={resetFilter}
          >
            <div className='offcanvas-item'>
              <Dropdown
                id='isGamificationVoucher'
                label='Voucher Type'
                name='isGamificationVoucher'
                options={GAMIFICATION_FILTER_OPTIONS}
                placeholder='Regular & Game Vouchers'
                value={filterValues.isGamificationVoucher}
                handleChange={(e) =>
                  setFilterValues({
                    ...filterValues,
                    isGamificationVoucher: e.target.value,
                  })
                }
              />
            </div>
          </FilterModel>
        )}
      </div>

      <FilterModel
        isFilterOpen={showModal}
        setIsFilterOpen={setShowModal}
        onSubmit={(e) => {
          e.preventDefault();
          if (values?.mobileNumber?.length === 10) {
            handleComplimentaryVoucherSubmit(selectedVoucher);
          } else {
            setError('Please enter a valid mobile number');
          }
        }}
        onCancel={onHideModal}
        filter
        heading='Complimentary Voucher'
        onHideFilter={onHideModal}
        resetfilter={resetValues}
      >
        <NumberInput
          name='mobileNumber'
          label='Mobile Number'
          errorText={error}
          value={values?.mobileNumber ?? ''}
          onChange={handleNumber}
          placeholder='Enter your mobile number'
          aria-invalid={!!error}
        />
        <NewInput
          name='serialNumber'
          label='Serial Number'
          value={values?.serialNumber ?? ''}
          onChange={setChange}
          placeholder='Enter Serial Number'
        />
      </FilterModel>

      {modalContent}

      <PageLoader pageLoading={pageLoading} />
    </>
  );
};

const PACKAGES_TAB = 'packages';
const VOUCHERS_TAB = 'vouchers';

// Shell: Vouchers and Packages tabs share this menu entry, permission and
// password prompt. Only the active pane is mounted so the hidden tab never
// fetches. The tab lives in the URL (not state) so navigate(-1) from the
// create/edit pages lands back on the Packages tab. The page heading lives here,
// above the tabs, so it never remounts or moves on a tab switch; the panes pass
// no title.
const VoucherManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab =
    searchParams.get('tab') === PACKAGES_TAB ? PACKAGES_TAB : VOUCHERS_TAB;

  // PaginationTable writes page and search into the URL and reads them back on
  // mount, so a tab switch replaces the parameters with the tab alone.
  const handleTabClick = (tab) => {
    if (tab === activeTab) return;
    setSearchParams({ tab }, { replace: true });
  };

  return (
    <>
      <div className='cms-content__container'>
        <h4>Voucher Management</h4>
        <div className='tabs tabs--voucher'>
          <button
            type='button'
            onClick={() => handleTabClick(VOUCHERS_TAB)}
            className={activeTab === VOUCHERS_TAB ? 'active' : ''}
          >
            Vouchers
          </button>
          <button
            type='button'
            onClick={() => handleTabClick(PACKAGES_TAB)}
            className={activeTab === PACKAGES_TAB ? 'active' : ''}
          >
            Bundles
          </button>
        </div>
      </div>
      {activeTab === PACKAGES_TAB ? <Packages /> : <VouchersPane />}
    </>
  );
};

export { GAMIFICATION_FILTER_OPTIONS };
export default VoucherManagement;
